/**
 * Local Cancel/Apply draft for the conditions editor.
 *
 * Editing happens against an in-memory list of `ConditionRowState` decoded
 * from the group's `conditions`. Apply rebuilds the conditions object
 * (preserving unknown keys); Cancel resets to the last applied snapshot.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { VoluumPathGroupConditions } from '@trafficpulse/shared';

import {
  applyRows,
  decodeConditions,
  getConditionDescriptor,
  type ConditionPredicate,
  type ConditionRowState,
} from './model';

export interface UseConditionsDraftResult {
  rows: ConditionRowState[];
  isDirty: boolean;
  addCondition: (key: string) => void;
  removeAt: (index: number) => void;
  setPredicateAt: (index: number, predicate: ConditionPredicate) => void;
  setValuesAt: (index: number, values: readonly string[]) => void;
  setTimeZoneAt: (index: number, timeZoneCode: string) => void;
  clearAll: () => void;
  reset: () => void;
  buildConditions: () => VoluumPathGroupConditions;
}

function snapshot(rows: ConditionRowState[]): ConditionRowState[] {
  return rows.map((r) => ({ ...r, values: [...r.values] }));
}

function conditionsSyncKey(
  conditions: VoluumPathGroupConditions | undefined,
): string {
  return JSON.stringify(conditions ?? {});
}

export function useConditionsDraft(
  conditions: VoluumPathGroupConditions | undefined,
): UseConditionsDraftResult {
  const syncKey = useMemo(() => conditionsSyncKey(conditions), [conditions]);

  const [initial, setInitial] = useState<ConditionRowState[]>(() =>
    decodeConditions(conditions),
  );
  const [rows, setRows] = useState<ConditionRowState[]>(() =>
    snapshot(initial),
  );

  useEffect(() => {
    const decoded = decodeConditions(conditions);
    setInitial(decoded);
    setRows(snapshot(decoded));
  }, [syncKey]);

  const addCondition = useCallback((key: string) => {
    const descriptor = getConditionDescriptor(key);
    if (!descriptor) return;
    setRows((prev) => {
      if (prev.some((r) => r.descriptor.key === key)) return prev;
      const base = { descriptor, predicate: 'MUST_BE' as const, values: [] };
      const row: ConditionRowState =
        descriptor.valueKind === 'weekdays'
          ? { ...base, timeZoneCode: 'UTC' }
          : base;
      return [...prev, row];
    });
  }, []);

  const removeAt = useCallback((index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const setPredicateAt = useCallback(
    (index: number, predicate: ConditionPredicate) =>
      setRows((prev) =>
        prev.map((r, i) => (i === index ? { ...r, predicate } : r)),
      ),
    [],
  );

  const setValuesAt = useCallback(
    (index: number, values: readonly string[]) =>
      setRows((prev) =>
        prev.map((r, i) => (i === index ? { ...r, values } : r)),
      ),
    [],
  );

  const setTimeZoneAt = useCallback((index: number, timeZoneCode: string) => {
    const code = timeZoneCode.trim();
    setRows((prev) =>
      prev.map((r, i) =>
        i === index && r.descriptor.valueKind === 'weekdays'
          ? { ...r, timeZoneCode: code || 'UTC' }
          : r,
      ),
    );
  }, []);

  const clearAll = useCallback(() => setRows([]), []);
  const reset = useCallback(() => setRows(snapshot(initial)), [initial]);

  const buildConditions = useCallback(
    () => applyRows(conditions ?? {}, rows),
    [conditions, rows],
  );

  return {
    rows,
    isDirty: !rowsEqual(rows, initial),
    addCondition,
    removeAt,
    setPredicateAt,
    setValuesAt,
    setTimeZoneAt,
    clearAll,
    reset,
    buildConditions,
  };
}

function weekdaysTimeZone(row: ConditionRowState): string {
  return row.descriptor.valueKind === 'weekdays'
    ? (row.timeZoneCode?.trim() || 'UTC')
    : '';
}

function rowsEqual(a: ConditionRowState[], b: ConditionRowState[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((row, i) => {
    const other = b[i];
    if (!other) return false;
    if (row.descriptor.key !== other.descriptor.key) return false;
    if (row.predicate !== other.predicate) return false;
    if (weekdaysTimeZone(row) !== weekdaysTimeZone(other)) return false;
    if (row.values.length !== other.values.length) return false;
    return row.values.every((v, idx) => v === other.values[idx]);
  });
}
